<template>
<div class="tk-col">
    <!-- Datos del ticket -->
    <div class="card p-1">
    <strong class="d-block mb-1">Datos del ticket</strong>
    <small class="d-block"><span class="text-muted">Falla:</span> {{ ticket.subject || "—" }}</small>
    <small class="d-block"><span class="text-muted">PMI:</span> {{ pmiLabel }}</small>
    <small class="d-block"><span class="text-muted">Proveedor:</span> {{ personLabel(ticket.provider, "Por asignar") }}</small>
    <small class="d-block"><span class="text-muted">Administrador:</span> {{ personLabel(ticket.administrator, "Por asignar") }}</small>
    </div>

    <!-- Análisis de la falla -->
    <div v-if="canEditAnalysis || ticket.failure_analysis" class="card p-1">
    <strong class="d-block mb-1">Análisis de la falla</strong>
    <template v-if="canEditAnalysis">
        <counted-textarea
        v-model="analysisText"
        rows="3"
        max-rows="8"
        placeholder="Describe el análisis de la falla reportada..."
        :maxlength="textLimits.analysis"
        />
        <div class="d-flex justify-content-end mt-1">
        <b-button
            variant="primary"
            size="sm"
            :disabled="savingAnalysis || !analysisText.trim()"
            @click="saveAnalysis"
        >
            {{ $t("actions.save") }}
        </b-button>
        </div>
    </template>
    <small v-else class="analysis-text">{{ ticket.failure_analysis }}</small>
    </div>

    <!-- Resolución -->
    <div v-if="ticket.root_cause" class="card p-1">
    <strong class="d-block mb-1">Resolución</strong>
    <small class="d-block analysis-text"><span class="text-muted">Causa raíz:</span> {{ ticket.root_cause }}</small>
    <small class="d-block"><span class="text-muted">Acción:</span> {{ resolutionActionLabel(ticket.resolution_action_type) }}</small>
    <small v-if="solutionEquipment" class="d-block">
        <span class="text-muted">Equipo nuevo:</span>
        {{ solutionEquipment.brand }} {{ solutionEquipment.model }} · Serie {{ solutionEquipment.serial_number }}
        <span v-if="solutionEquipment.description">({{ solutionEquipment.description }})</span>
    </small>
    </div>

    <!-- Historial -->
    <div class="history-card card p-1">
    <div class="d-flex align-items-center justify-content-between mb-1">
        <strong>Historial</strong>
        <small class="text-muted">{{ sortedHistory.length }} entradas</small>
    </div>

    <div v-if="sortedHistory.length" class="history-scroll">
        <div v-for="s in sortedHistory" :key="s.id" class="history-row">
        <b-badge pill :variant="badgeFor(s.name)" class="history-badge">
            {{ s.name }}
        </b-badge>
        <div class="history-user">
            <div class="user-name">{{ (s.user && s.user.name) || "—" }}</div>
            <div v-if="s.user && s.user.email" class="user-email text-muted">
            {{ s.user.email }}
            </div>
        </div>
        <small class="history-date text-muted">{{ formatDate(s.created_at) }}</small>
        <small v-if="s.comment" class="history-note text-muted">{{ s.comment }}</small>
        </div>
    </div>
    <div v-else class="text-muted small py-1">Sin movimientos</div>
    </div>

    <!-- Editar ticket (admin) / solo lectura para los demás -->
    <div class="card p-1">
    <strong class="d-block mb-2">{{ canEditTicket ? "Editar ticket" : "Información del ticket" }}</strong>

    <b-form-group :label="$t('helpdesk.ticket.sinister')" label-for="edit-classification" class="mb-1">
        <v-select
        id="edit-classification"
        v-model="editForm.classification"
        :options="classificationOptions"
        :reduce="(o) => o.value"
        label="label"
        :clearable="false"
        :disabled="!canEditTicket"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        @input="onEditClassificationChange"
        />
    </b-form-group>

    <b-form-group v-if="editWithOrigin" label="Origen" label-for="edit-origin" class="mb-1">
        <v-select
        id="edit-origin"
        v-model="editForm.originId"
        :options="originOptions"
        :reduce="(o) => o.value"
        label="label"
        :clearable="false"
        :loading="loadingCategories"
        :disabled="!canEditTicket"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        @input="onEditOriginChange"
        />
    </b-form-group>

    <b-form-group :label="$t('helpdesk.ticket.sinistertype')" label-for="edit-type" class="mb-1">
        <v-select
        id="edit-type"
        v-model="editForm.typeId"
        :options="editTypeOptions"
        :reduce="(o) => o.value"
        label="label"
        :loading="loadingCategories"
        :disabled="!canEditTicket || !editForm.classification"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        @input="editForm.subtypeId = null"
        />
    </b-form-group>

    <b-form-group :label="$t('helpdesk.ticket.sinistersubtype')" label-for="edit-subtype" class="mb-1">
        <v-select
        id="edit-subtype"
        v-model="editForm.subtypeId"
        :options="editSubtypeOptions"
        :reduce="(o) => o.value"
        label="label"
        :disabled="!canEditTicket || !editSubtypeOptions.length"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        />
    </b-form-group>

    <b-form-group label="Prioridad" label-for="edit-priority" class="mb-1">
        <v-select
        id="edit-priority"
        v-model="editForm.priority"
        :options="editPriorityOptions"
        :reduce="(o) => o.value"
        label="label"
        :clearable="false"
        :disabled="true"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        />
    </b-form-group>

    <!-- PMI (unidades) - múltiple -->
    <b-form-group :label="$t('helpdesk.ticket.relatedElement')" label-for="edit-unit" class="mb-1">
        <v-select
        id="edit-unit"
        v-model="editForm.unitIds"
        :options="unitOptions"
        :reduce="(o) => o.value"
        label="label"
        :filterable="false"
        :multiple="true"
        :loading="loadingUnits"
        :disabled="!canEditTicket"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        @search="onUnitSearch"
        @input="onEditUnitsChange"
        />
    </b-form-group>

    <!-- Cámaras por cada PMI seleccionado (opcional) -->
    <b-form-group
        v-if="editForm.unitIds.length"
        :label="$t('helpdesk.ticket.relatedCamera')"
        label-for="edit-camera"
        class="mb-1"
    >
        <b-form-checkbox
        v-model="editForm.hasRelatedCamera"
        name="edit-has-related-camera"
        class="mr-0 mb-1"
        switch
        inline
        :disabled="!canEditTicket"
        @change="onToggleCameras"
        >
        {{ $t("helpdesk.ticket.relatedCamera") }}
        </b-form-checkbox>

        <template v-if="editForm.hasRelatedCamera">
        <div
            v-for="unitId in editForm.unitIds"
            :key="unitId"
            class="mb-1 pl-1 border-left"
        >
            <small class="text-muted d-block mb-25">{{ unitLabel(unitId) }}</small>
            <v-select
            :id="`edit-camera-${unitId}`"
            v-model="editForm.camerasByUnit[unitId]"
            :options="camerasByUnitOptions[unitId] || []"
            :reduce="(o) => o.value"
            label="label"
            :multiple="true"
            :loading="!!loadingCamerasByUnit[unitId]"
            :disabled="!canEditTicket"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            />
        </div>
        </template>
    </b-form-group>

    <b-form-group :label="$t('helpdesk.ticket.description')" label-for="edit-message" class="mb-1">
        <counted-textarea
        id="edit-message"
        v-model="editForm.message"
        rows="3"
        max-rows="6"
        :disabled="!canEditTicket"
        :maxlength="textLimits.description"
        />
    </b-form-group>

    <div v-if="canEditTicket" class="d-flex justify-content-between">
        <b-button variant="primary" :disabled="!canSaveEdit" @click="onSave">
        {{ $t("actions.save") }}
        </b-button>
    </div>
    </div>
</div>
</template>

<script>
import { BButton, BBadge, BFormGroup, BFormCheckbox } from "bootstrap-vue";
import vSelect from "vue-select";
import moment from "moment-timezone";
import { ref, computed, onMounted, set, del } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axios from "@axios";
import store from "@/store";
import CountedTextarea from "@/views/helpdesk/CountedTextarea.vue";
import { TEXT_LIMITS } from "@/views/helpdesk/textLimits";
import useTicketCatalogs, { CLASSIFICATION_OPTIONS } from "./useTicketCatalogs";
import useUnitSearch from "@/views/helpdesk/useUnitSearch";
import { showOrigin } from "@/views/helpdesk/catalogConfig";

const RESOLUTION_ACTIONS = {
reconfiguration: "Reconfiguración",
partial_replacement: "Reemplazo parcial",
total_replacement: "Sustitución total",
};

export default {
name: "TicketInfoColumn",
components: {
    BButton,
    BBadge,
    BFormGroup,
    CountedTextarea,
    BFormCheckbox,
    vSelect,
},
props: {
    ticket: { type: Object, required: true },
    raw: { type: Object, default: null },
    ticketId: { type: [Number, String], required: true },
    sortedHistory: { type: Array, default: () => [] },
    canEditTicket: { type: Boolean, default: false },
    canEditAnalysis: { type: Boolean, default: false },
    canUpdate: { type: Boolean, default: false },
},
setup(props, { emit }) {
    const toast = useToast();
    const toastResult = (title, ok) =>
    toast({
        component: ToastificationContent,
        props: {
        title,
        icon: ok ? "CheckIcon" : "AlertTriangleIcon",
        variant: ok ? "success" : "danger",
        },
    });

    const {
    originOptions,
    typeOptionsFor,
    subtypeOptionsFor,
    loadingCategories,
    priorityOptionsWith,
    ensurePriorities,
    loadCategories,
    loadPriorities,
    splitCategory,
    } = useTicketCatalogs();
    const { unitOptions, loadingUnits, searchUnits, onUnitSearch, pinUnits } = useUnitSearch();
    const classificationOptions = CLASSIFICATION_OPTIONS;

    // ── Formulario de edición ──
    const editForm = ref({
    classification: null,
    originId: null,
    typeId: null,
    subtypeId: null,
    priority: null,
    message: "",
    // ── Múltiple (igual que CreateTicketModal) ──
    unitIds: [], // [13604, 13605]
    hasRelatedCamera: false,
    camerasByUnit: {}, // { 13604: [48077], 13605: [48171] }
    });
    const savingEdit = ref(false);

    // Opciones de cámaras por unidad y su estado de carga (local al componente)
    const camerasByUnitOptions = ref({}); // { 13604: [{value,label}] }
    const loadingCamerasByUnit = ref({}); // { 13604: true/false }

    const editWithOrigin = computed(() => showOrigin(editForm.value.classification));
    const editTypeOptions = computed(() =>
    typeOptionsFor(editWithOrigin.value ? editForm.value.originId : null)
    );
    const editSubtypeOptions = computed(() => subtypeOptionsFor(editForm.value.typeId));
    const editPriorityOptions = computed(() => priorityOptionsWith(editForm.value.priority));

    onMounted(() => {
    loadPriorities().catch(() => {});
    searchUnits();
    });

    // ── PMI múltiple ──
    const unitLabel = (unitId) => {
    const found = unitOptions.value.find((u) => u.value === unitId);
    return found ? found.label : `PMI ${unitId}`;
    };

    const loadCamerasForUnit = async (unitId) => {
    if (camerasByUnitOptions.value[unitId]) return; // ya cargadas
    set(loadingCamerasByUnit.value, unitId, true);
    try {
        const { data } = await axios.get("v1/proxy_camera", {
        params: { units: [unitId], max: 1000 },
        });
        const options = (data.success ? data.data : []).map((c) => ({
        value: c.id,
        label: (c.camera && c.camera.name) || `Cámara ${c.id}`,
        }));
        // Conserva las cámaras ya seleccionadas aunque no vengan en la lista
        const selected = editForm.value.camerasByUnit[unitId] || [];
        selected.forEach((id) => {
        if (!options.some((o) => o.value === id)) options.push({ value: id, label: `Cámara #${id}` });
        });
        set(camerasByUnitOptions.value, unitId, options);
        if (!editForm.value.camerasByUnit[unitId]) {
        set(editForm.value.camerasByUnit, unitId, []);
        }
    } catch (e) {
        toastResult("No se pudieron cargar las cámaras del PMI.", false);
    } finally {
        set(loadingCamerasByUnit.value, unitId, false);
    }
    };

    const onEditUnitsChange = (unitIds) => {
    (unitIds || []).forEach((id) => pinUnits && pinUnits(id));

    // Limpia cámaras de unidades que ya no están seleccionadas
    Object.keys(editForm.value.camerasByUnit).forEach((id) => {
        if (!unitIds.includes(Number(id)) && !unitIds.includes(id)) {
        del(editForm.value.camerasByUnit, id);
        del(camerasByUnitOptions.value, id);
        }
    });

    // Carga cámaras de las nuevas, si el switch está activo
    if (editForm.value.hasRelatedCamera) {
        unitIds.forEach((id) => loadCamerasForUnit(id));
    }
    };

    const onToggleCameras = (checked) => {
    if (!checked) {
        editForm.value.camerasByUnit = {};
        camerasByUnitOptions.value = {};
        return;
    }
    editForm.value.unitIds.forEach((id) => loadCamerasForUnit(id));
    };

    const fillEditForm = async (tk) => {
    const { originId, typeId, subtypeId } = splitCategory(tk.failure_category);

    // Precarga desde tk.units: cada elemento ES la unidad (u.id, u.description)
    // con sus cámaras en u.cameras[] (cada una con u.cameras[].id = id de proxy).
    // El nombre de la cámara no viene en el detalle; lo resuelve loadCamerasForUnit.
    // Se dejan fallbacks (u.id_unit / u.unit) por si el shape cambia.
    const existingUnits = Array.isArray(tk.units) ? tk.units : [];
    const unitIds = [];
    const camerasByUnit = {};

    if (existingUnits.length) {
        existingUnits.forEach((u) => {
        const uid = u.id || u.id_unit || (u.unit && u.unit.id);
        if (!uid) return;
        unitIds.push(uid);
        const cams = (u.cameras || u.id_cameras || []).map((c) =>
            c && typeof c === "object" ? c.id : c
        );
        camerasByUnit[uid] = cams;
        const label = u.description || (u.unit && u.unit.description) || `PMI ${uid}`;
        pinUnits({ value: uid, label });
        });
    } else if (tk.unit) {
        unitIds.push(tk.unit.id);
        camerasByUnit[tk.unit.id] = tk.id_camera ? [tk.id_camera] : [];
        pinUnits({ value: tk.unit.id, label: tk.unit.description });
    }

    const hasRelatedCamera = Object.values(camerasByUnit).some((a) => a.length);

    editForm.value = {
        classification: tk.classification || null,
        originId,
        typeId,
        subtypeId,
        priority: tk.id_priority,
        message: tk.message || "",
        unitIds,
        hasRelatedCamera,
        camerasByUnit,
    };

    try {
        await Promise.all([ensurePriorities(), loadCategories(tk.classification)]);
    } catch (e) {
        // los selects quedan vacíos; el resto sigue
    }

    // Si ya tenía cámaras, carga las opciones de cada PMI para mostrar sus nombres
    if (hasRelatedCamera) {
        unitIds.forEach((id) => loadCamerasForUnit(id));
    }
    };

    const onEditClassificationChange = async (catalog) => {
    editForm.value.originId = null;
    editForm.value.typeId = null;
    editForm.value.subtypeId = null;
    await loadCategories(catalog);
    };

    const onEditOriginChange = () => {
    editForm.value.typeId = null;
    editForm.value.subtypeId = null;
    };

    const canSaveEdit = computed(() => {
    const f = editForm.value;
    if (!props.canEditTicket || savingEdit.value) return false;
    if (!f.priority || !f.typeId || !f.unitIds.length || !f.message.trim()) return false;
    return editSubtypeOptions.value.length ? !!f.subtypeId : true;
    });

    const onSave = () => {
    const f = editForm.value;
    // ── AJUSTAR (payload): mismo formato que el create (units[] + id_cameras[]).
    // El endpoint helpdesk/editTicket debe aceptar el arreglo "units".
    const params = {
        id_priority: f.priority,
        id_failure_category: f.subtypeId || f.typeId,
        message: f.message,
        units: f.unitIds.map((unitId) => ({
        id_unit: unitId,
        id_cameras: (f.hasRelatedCamera && f.camerasByUnit[unitId]) || [],
        })),
    };
    savingEdit.value = true;
    store
        .dispatch("helpdesk/editTicket", { id: props.ticketId, params })
        .then((response) => {
        toastResult((response && response.message) || "Ticket actualizado.", true);
        emit("reload");
        })
        .catch((error) => {
        toastResult(error?.response?.data?.message || "No se pudo actualizar el ticket.", false);
        })
        .finally(() => {
        savingEdit.value = false;
        });
    };

    // ── Análisis de la falla ──
    const analysisText = ref(props.ticket.failure_analysis || "");
    const savingAnalysis = ref(false);
    const saveAnalysis = () => {
    savingAnalysis.value = true;
    store
        .dispatch("helpdesk/saveFailureAnalysis", {
        id: props.ticketId,
        params: { failure_analysis: analysisText.value.trim() },
        })
        .then((response) => {
        toastResult((response && response.message) || "Análisis guardado.", true);
        emit("reload");
        })
        .catch((error) => {
        toastResult(error?.response?.data?.message || "No se pudo guardar el análisis.", false);
        })
        .finally(() => {
        savingAnalysis.value = false;
        });
    };

    // ── Resolución (solo lectura) ──
    const resolutionActionLabel = (value) => RESOLUTION_ACTIONS[value] || value || "—";
    const solutionEquipment = computed(() => {
    const tk = props.raw && props.raw.ticket;
    if (tk && tk.new_equipment && typeof tk.new_equipment === "object") return tk.new_equipment;
    const events = (tk && tk.events) || [];
    const solutions = events.filter((e) => e.glpi_payload && e.glpi_payload.type === "solution");
    const last = solutions[solutions.length - 1];
    return (last && last.glpi_payload.equipment) || null;
    });

    // PMI(s) del ticket: ahora viene como arreglo tk.units (cada u = la unidad).
    // Se dejan fallbacks al singular tk.unit por compatibilidad.
    const pmiLabel = computed(() => {
    const units = (props.ticket && props.ticket.units) || [];
    if (units.length) {
        return units
        .map((u) => u.description || (u.unit && u.unit.description) || `PMI ${u.id || u.id_unit}`)
        .join(", ");
    }
    if (props.ticket && props.ticket.unit) return props.ticket.unit.description || "—";
    return "—";
    });

    const personLabel = (user, empty = "—") =>
    user ? [user.name, user.first_name].filter(Boolean).join(" ") || user.email : empty;

    const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "—");

    const badgeFor = (name) => {
    const map = {
        Nuevo: "primary",
        Asignado: "info",
        "En atención": "info",
        "En espera": "warning",
        Resuelto: "success",
        "En validación": "warning",
        Cerrado: "secondary",
        NEW: "primary",
    };
    return map[name] || "secondary";
    };

    // Inicializa el form con el ticket recibido
    fillEditForm(props.ticket);

    return {
    textLimits: TEXT_LIMITS,
    // edición
    classificationOptions,
    originOptions,
    editForm,
    editWithOrigin,
    editTypeOptions,
    editSubtypeOptions,
    editPriorityOptions,
    loadingCategories,
    unitOptions,
    loadingUnits,
    onUnitSearch,
    onEditClassificationChange,
    onEditOriginChange,
    canSaveEdit,
    onSave,
    // PMI / cámaras múltiples
    unitLabel,
    camerasByUnitOptions,
    loadingCamerasByUnit,
    onEditUnitsChange,
    onToggleCameras,
    // análisis
    analysisText,
    savingAnalysis,
    saveAnalysis,
    // resolución
    resolutionActionLabel,
    solutionEquipment,
    // helpers
    pmiLabel,
    personLabel,
    formatDate,
    badgeFor,
    };
},
};
</script>

<style scoped>
/* Layout de columna */
.tk-col {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
}
.tk-col > .card {
    margin-bottom: 0;
    min-height: 0;
}
.info-card,
.history-card {
    background: #f9fafb;
    border: 1px solid #eceff3;
    border-radius: 8px;
}

/* Texto de análisis / resolución */
.analysis-text {
    white-space: pre-wrap;
}

/* Historial scrolleable dentro de su tarjeta */
.history-card {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: 200px;
    max-height: 200px;
}
.history-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    min-height: 0;
    padding-right: 4px;
}
.history-scroll::-webkit-scrollbar {
    width: 6px;
}
.history-scroll::-webkit-scrollbar-thumb {
    background: #cfd4da;
    border-radius: 3px;
}
.history-row {
    display: grid;
    grid-template-columns: max-content 1fr max-content;
    gap: 8px;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px dashed #eee;
}
.history-row:last-child {
    border-bottom: 0;
}
.history-note {
    grid-column: 1 / -1;
}
.history-badge {
    display: inline-flex;
    align-items: center;
}
.user-name {
    font-size: 0.85rem;
}
.user-email {
    font-size: 0.75rem;
}
.history-date {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

/* Pills del v-select (PMI múltiple): forzar fondo azul siempre,
    evita que a veces salgan en blanco según la variante del tema. */
.tk-col ::v-deep .vs__selected {
    background-color: #1b3d5e !important;
    border-color: #1b3d5e !important;
    color: #ffffff !important;
}
.tk-col ::v-deep .vs__selected .vs__deselect,
.tk-col ::v-deep .vs__selected .vs__deselect svg,
.tk-col ::v-deep .vs__selected .vs__deselect path {
    fill: #ffffff !important;
    stroke: #ffffff !important;
    opacity: 0.9;
}
/* En modo disabled, mantenerlas legibles */
.tk-col ::v-deep .vs--disabled .vs__selected {
    background-color: #1b3d5e !important;
    color: #ffffff !important;
    opacity: 0.65;
}
</style>